import { useState } from "react";
import { useLanguage } from "@/contexts/LanguageContext";
import { Material } from "@/types/material/material.types";
import { Button } from "@/components/ui/button";
import { DataTable } from "@/components/ui/data-table/index";
import { TableHeader } from "@/components/ui/table-header/index";
import { Plus } from "lucide-react";
import { MaterialForm } from "./materials/MaterialForm";
import { useMaterialTable } from "@/hooks/materials/useMaterialTable";
import { useMaterialColumns } from "@/components/setup/materials/columns";
import { useToast } from "@/components/ui/use-toast";
import { useAuth } from "@/contexts/AuthContext";
import { materialService } from "@/services/material/materialService";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";

export const Materials = () => {
  const { t } = useLanguage();
  const { toast } = useToast();
  const { user, claims } = useAuth();
  const isAssimagraUser = claims?.role === "assimagra";
  const [formOpen, setFormOpen] = useState(false);
  const [selectedMaterial, setSelectedMaterial] = useState<Material | null>(null);
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [materialToDelete, setMaterialToDelete] = useState<Material | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);

  const {
    filteredMaterials,
    loading,
    refetch,
    searchTerm,
    setSearchTerm,
    nameFilters,
    setNameFilters,
    clearFilters,
    nameOptions,
  } = useMaterialTable();

  const handleAddMaterial = () => {
    setSelectedMaterial(null);
    setFormOpen(true);
  };

  const handleEditMaterial = (material: Material) => {
    setSelectedMaterial(material);
    setFormOpen(true);
  };

  const handleDeleteMaterial = (material: Material) => {
    setMaterialToDelete(material);
    setDeleteConfirmOpen(true);
  };

  const confirmDelete = async () => {
    if (!materialToDelete?.id || !user?.uid) {
      return;
    }

    setIsDeleting(true);

    try {
      await materialService.delete(materialToDelete.id, user.uid);

      toast({
        title: t("materials.deleted"),
        description: `${materialToDelete.name} ${t("materials.toasts.deleteSuccess.description")}`,
      });

      refetch();
      setDeleteConfirmOpen(false);
      setMaterialToDelete(null);
    } catch (error) {
      toast({
        title: t("materials.errors.deleteError"),
        description: (error as Error).message,
        variant: "destructive",
      });
    } finally {
      setIsDeleting(false);
    }
  };

  const columns = useMaterialColumns(t, handleEditMaterial, handleDeleteMaterial);

  if (loading) {
    return (
      <div className="flex items-center justify-center p-8">
        <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-primary"></div>
      </div>
    );
  }

  const filters = [
    {
      label: t("materials.filters.name"),
      options: nameOptions,
      selectedValues: nameFilters,
      onChange: setNameFilters,
      showSearch: true,
    },
  ];

  return (
    <div>
      <div className="flex flex-col sm:flex-row sm:items-center justify-between mb-4 gap-2">
        <h2 className="text-lg font-semibold">{t("materials.title")}</h2>
        {!isAssimagraUser && (
          <Button
            onClick={handleAddMaterial}
            className="bg-teal-600 text-white hover:bg-teal-700 self-start sm:self-auto"
          >
            <Plus className="h-4 w-4 mr-2" />
            {t("materials.addButton")}
          </Button>
        )}
      </div>

      <TableHeader
        searchTerm={searchTerm}
        onSearchChange={setSearchTerm}
        searchPlaceholder={t("materials.searchPlaceholder")}
        filters={filters}
        onClearAll={clearFilters}
      />

      <div className="overflow-hidden">
        <DataTable columns={columns} data={filteredMaterials} pageSize={10} className="w-full" minWidth="auto" />
      </div>

      <MaterialForm
        isOpen={formOpen}
        onClose={() => setFormOpen(false)}
        materialToEdit={selectedMaterial}
        onSuccess={refetch}
      />

      {/* Delete confirmation dialog */}
      <Dialog open={deleteConfirmOpen} onOpenChange={setDeleteConfirmOpen}>
        <DialogContent className="sm:max-w-[425px]">
          <DialogHeader>
            <DialogTitle>{t("materials.confirmDelete.title")}</DialogTitle>
          </DialogHeader>
          <p className="py-4">{t("materials.confirmDelete.description")}</p>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => {
                setDeleteConfirmOpen(false);
                setMaterialToDelete(null);
              }}
              disabled={isDeleting}
            >
              {t("materials.cancel")}
            </Button>
            <Button variant="destructive" onClick={confirmDelete} disabled={isDeleting}>
              {isDeleting ? t("materials.confirmDelete.actionLoading") : t("materials.confirmDelete.action")}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};
